const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || "http://localhost:1337";


export const api = {
  // Get all projects or specific projects by IDs
  async getProjects(ids = null, page = 1, pageSize = 9, category = null) {
    try {
      let url = `${STRAPI_URL}/api/projects?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=date:desc`;

      // If specific IDs are provided, add filter query
      if (ids && Array.isArray(ids) && ids.length > 0) {
        const filterParams = ids
          .map((id, index) => `filters[id][$in][${index}]=${id}`)
          .join("&");
        url += `&${filterParams}`;
      }

      // Add category filter if provided and not 'Alle'
      if (category && category !== "Alle") {
        url += `&filters[category][$eq]=${encodeURIComponent(category)}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      return {
        data: data.data || [],
        pagination: data.meta?.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching projects:", error);
      return { data: [], pagination: {} };
    }
  },

  // Get all projects or specific projects by slugs
  async getProjectsBySlugs(slugs = null) {
    try {
      let url = `${STRAPI_URL}/api/projects?populate=*`;

      // If specific slugs are provided, add filter query
      if (slugs && Array.isArray(slugs) && slugs.length > 0) {
        const filterParams = slugs
          .map((slug, index) => `filters[slug][$in][${index}]=${slug}`)
          .join("&");
        url += `&${filterParams}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching projects by slugs:", error);
      return [];
    }
  },

  // Get a single project by ID
  async getProject(id) {
    try {
      const response = await fetch(
        `${STRAPI_URL}/api/projects/${id}?populate=*`
      );
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching project:", error);
      return null;
    }
  },

  // Get a single project by slug
  async getProjectBySlug(slug) {
    try {
      const response = await fetch(
        `${STRAPI_URL}/api/projects?populate=*&filters[slug][$eq]=${slug}`
      );
      const data = await response.json();
      return data.data && data.data.length > 0 ? data.data[0] : null;
    } catch (error) {
      console.error("Error fetching project by slug:", error);
      return null;
    }
  },

  // Get related articles by combining specific slugs with tag-based recommendations
  async getRelatedProjects(specificSlugs = [], currentSlug = null, limit = 9) {
    try {
      let relatedProjects = [];

      // 1. First, get the specifically requested articles (from relevantPosts)
      if (specificSlugs.length > 0) {
        const specificResponse = await this.getProjectsBySlugs(specificSlugs);
        relatedProjects = specificResponse;
      }

      // 2. If we have a current article, get its tags to find similar articles
      if (currentSlug && relatedProjects.length < limit) {
        const currentProject = await this.getProjectBySlug(currentSlug);
        if (
          currentProject &&
          currentProject.tags &&
          currentProject.tags.length > 0
        ) {
          const currentTags = transformProject(currentProject).tags;

          // Get all articles to filter by tags client-side (could be optimized with backend filtering later)
          const allProjects = await this.getProjects();
          const allTransformed = allProjects.data.map(transformProject);

          // Find articles with at least one common tag
          const tagMatches = allTransformed.filter((project) => {
            // Skip current article and already included articles
            if (project.slug === currentSlug) return false;
            if (
              relatedProjects.some((existing) => existing.slug === project.slug)
            )
              return false;

            // Check for common tags
            return project.tags.some((tag) => currentTags.includes(tag));
          });

          // Sort by number of common tags (most relevant first)
          tagMatches.sort((a, b) => {
            const aCommonTags = a.tags.filter((tag) =>
              currentTags.includes(tag)
            ).length;
            const bCommonTags = b.tags.filter((tag) =>
              currentTags.includes(tag)
            ).length;
            return bCommonTags - aCommonTags;
          });

          // Add tag-based matches to fill remaining slots
          const remainingSlots = limit - relatedProjects.length;
          relatedProjects = [
            ...relatedProjects,
            ...tagMatches.slice(0, remainingSlots),
          ];
        }
      }

      // 3. If still not enough, fill with latest articles from same category
      if (relatedProjects.length < limit && currentSlug) {
        const currentProject = await this.getProjectBySlug(currentSlug);
        if (currentProject) {
          const currentCategory = transformProject(currentProject).category;

          const categoryResponse = await this.getProjects(
            null,
            1,
            limit,
            currentCategory
          );
          const categoryProjects = categoryResponse.data.map(transformProject);

          const categoryMatches = categoryProjects.filter((project) => {
            if (project.slug === currentSlug) return false;
            return !relatedProjects.some(
              (existing) => existing.slug === project.slug
            );
          });

          const remainingSlots = limit - relatedProjects.length;
          relatedProjects = [
            ...relatedProjects,
            ...categoryMatches.slice(0, remainingSlots),
          ];
        }
      }

      return relatedProjects.slice(0, limit);
    } catch (error) {
      console.error("Error fetching related projects:", error);
      return [];
    }
  },

  // Get all inserate (advertisements)
  async getInserate() {
    try {
      const response = await fetch(`${STRAPI_URL}/api/inserate?populate=*`);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching inserate:", error);
      return [];
    }
  },

  // Get all blog posts
  async getBlogPosts(page = 1, pageSize = 10) {
    try {
      const url = `${STRAPI_URL}/api/blog-posts?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=PublishDate:desc`;
      const response = await fetch(url);
      const data = await response.json();
      return {
        data: data.data || [],
        pagination: data.meta?.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      return { data: [], pagination: {} };
    }
  },

  // Get a single blog post by slug
  async getBlogPostBySlug(slug) {
    try {
      const url = `${STRAPI_URL}/api/blog-posts?filters[Slug][$eq]=${slug}&populate=*`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.data && data.data.length > 0) {
        return data.data[0];
      }
      return null;
    } catch (error) {
      console.error("Error fetching blog post:", error);
      return null;
    }
  },

  // Get related blog posts (excluding current post)
  async getRelatedBlogPosts(currentSlug, limit = 3) {
    try {
      const url = `${STRAPI_URL}/api/blog-posts?filters[Slug][$ne]=${currentSlug}&populate=*&pagination[limit]=${limit}&sort=PublishDate:desc`;
      const response = await fetch(url);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching related blog posts:", error);
      return [];
    }
  },

  // Get blog posts by slugs
  async getBlogPostsBySlugs(slugs) {
    if (!slugs || !Array.isArray(slugs) || slugs.length === 0) {
      return [];
    }

    try {
      // Create filter parameters for each slug
      const filterParams = slugs
        .map(
          (slug, index) =>
            `filters[Slug][$in][${index}]=${encodeURIComponent(slug)}`
        )
        .join("&");

      // Remove sort parameter to preserve original order
      const url = `${STRAPI_URL}/api/blog-posts?populate=*&${filterParams}`;
      const response = await fetch(url);
      const data = await response.json();
      const posts = data.data || [];
      
      // Sort posts to match the original slug order
      const sortedPosts = [];
      slugs.forEach(slug => {
        const post = posts.find(p => {
          const postSlug = p.attributes?.Slug || p.attributes?.slug || p.Slug || p.slug;
          return postSlug === slug;
        });
        if (post) {
          sortedPosts.push(post);
        }
      });
      return sortedPosts;
    } catch (error) {
      console.error("Error fetching blog posts by slugs:", error);
      return [];
    }
  },

  // Get Instagram posts
  async getInstagramPosts() {
    try {
      const response = await fetch(
        `${STRAPI_URL}/api/instagram-posts?populate=image&sort=order:asc,publishedAt:desc`
      );
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error("Error fetching Instagram posts:", error);
      return [];
    }
  },

  // Get a single inserat by ID
  async getInserat(id) {
    try {
      const response = await fetch(
        `${STRAPI_URL}/api/inserate/${id}?populate=*`
      );
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error fetching inserat:", error);
      return null;
    }
  },

  // Create a new project (admin only)
  async createProject(projectData, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: projectData }),
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating project:", error);
      throw error;
    }
  },

  // Create a new inserat (admin only)
  async createInserat(inseratData, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/inserate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: inseratData }),
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error creating inserat:", error);
      throw error;
    }
  },

  // Update a project (admin only)
  async updateProject(id, projectData, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/projects/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: projectData }),
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating project:", error);
      throw error;
    }
  },

  // Update an inserat (admin only)
  async updateInserat(id, inseratData, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/inserate/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ data: inseratData }),
      });
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("Error updating inserat:", error);
      throw error;
    }
  },

  // Delete a project (admin only)
  async deleteProject(id, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/projects/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.ok;
    } catch (error) {
      console.error("Error deleting project:", error);
      throw error;
    }
  },

  // Delete an inserat (admin only)
  async deleteInserat(id, token) {
    try {
      const response = await fetch(`${STRAPI_URL}/api/inserate/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.ok;
    } catch (error) {
      console.error("Error deleting inserat:", error);
      throw error;
    }
  },
};

// Helper function to generate slug from title
const generateSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Remove multiple consecutive hyphens
    .trim();
};

// Helper function to transform Strapi blog post data
export const transformBlogPost = (strapiBlogPost) => {
  if (!strapiBlogPost) return null;

  const attributes = strapiBlogPost.attributes || strapiBlogPost;

  // Handle hero image - could be Cloudinary URL or Strapi upload
  const getImageUrl = (imageObj) => {
    if (!imageObj?.url) return null;

    // If it's already a full URL (Cloudinary), return as-is
    if (imageObj.url.startsWith("http")) {
      return imageObj.url;
    }

    // If it's a relative path (local Strapi), prepend STRAPI_URL
    return `${STRAPI_URL}${imageObj.url}`;
  };

  // Handle both capitalized and lowercase field names
  let heroImage = null;

  // Check if HeroImage/heroImage has nested data structure (Strapi v4 format)
  if (attributes.HeroImage?.data || attributes.heroImage?.data) {
    const imageData = attributes.HeroImage?.data || attributes.heroImage?.data;
    heroImage = getImageUrl(imageData.attributes || imageData);
  }
  // Check if it's a direct image object (attributes.HeroImage is the image object itself)
  else if (attributes.HeroImage?.url || attributes.heroImage?.url) {
    const imageObj = attributes.HeroImage || attributes.heroImage;
    heroImage = getImageUrl(imageObj);
  }
  // Check if it's already a URL string
  else if (typeof (attributes.HeroImage || attributes.heroImage) === "string") {
    heroImage = attributes.HeroImage || attributes.heroImage;
  }

  return {
    id: strapiBlogPost.id,
    title: attributes.Title || attributes.title || "",
    slug: attributes.Slug || attributes.slug || "",
    content: attributes.Content || attributes.content || "",
    excerpt: attributes.Excerpt || attributes.excerpt || "",
    publishDate:
      attributes.PublishDate || attributes.publishDate || attributes.createdAt,
    author:
      attributes.Author ||
      attributes.Auhtor ||
      attributes.author ||
      "WTM Sportwagen Team", // Note: "Auhtor" typo in schema
    heroImage: heroImage,
    aspectRatio: attributes.AspectRatio || attributes.aspectRatio || 1.6, // Default to 16:10 ratio
    tags: attributes.tags || [],
    // Handle related posts if populated
    relatedPosts: attributes.relatedPosts?.data
      ? attributes.relatedPosts.data.map(transformBlogPost)
      : [],
  };
};

// Helper function to transform Strapi data to match your current format
export const transformProject = (strapiProject) => {
  if (!strapiProject) return null;

  // Handle both flat structure (current) and nested attributes structure
  const attributes = strapiProject.attributes || strapiProject;

  // Handle rich text content (preserve markdown/rich text formatting)
  const getDescription = (desc) => {
    if (!desc) return "";
    if (typeof desc === "string") return desc;
    if (desc.data) return desc.data;
    if (Array.isArray(desc)) {
      // Handle Strapi's rich text format
      return desc
        .map((block) => {
          if (block.type === "paragraph") {
            return block.children.map((child) => child.text).join("");
          }
          return "";
        })
        .join("\n\n");
    }
    return "";
  };

  // Generate slug from title if slug is missing
  const generatedSlug = generateSlug(attributes.title);

  // Handle Cloudinary URLs (full URLs) vs local Strapi uploads (relative paths)
  const getImageUrl = (imageObj) => {
    if (!imageObj?.url) return null;

    // If it's already a full URL (Cloudinary), return as-is
    if (imageObj.url.startsWith("http")) {
      return imageObj.url;
    }

    // If it's a relative path (local Strapi), prepend STRAPI_URL
    return `${STRAPI_URL}${imageObj.url}`;
  };

  return {
    id: strapiProject.id,
    title: attributes.title || "",
    description: getDescription(attributes.description),
    shortDescription: attributes.shortDescription || "",
    image: getImageUrl(attributes.image) || attributes.image || null,
    gallery: Array.isArray(attributes.gallery)
      ? attributes.gallery.map((item) => getImageUrl(item)).filter(Boolean)
      : [],
    date: attributes.date,
    category: attributes.category || "",
    tags:
      attributes.tags && typeof attributes.tags === "string"
        ? attributes.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [],
    slug: attributes.slug || generatedSlug, // Use backend slug or generate from title
  };
};

// Helper function to transform Inserat data
export const transformInserat = (strapiInserat) => {
  if (!strapiInserat) return null;

  const attributes = strapiInserat.attributes || strapiInserat;

  // Handle rich text content
  const getRichText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    if (text.data) return text.data;
    if (Array.isArray(text)) {
      // Handle Strapi's rich text format
      return text
        .map((block) => {
          if (block.type === "paragraph") {
            return block.children
              .map((child) => {
                if (child.bold) return `**${child.text}**`;
                if (child.italic) return `*${child.text}*`;
                return child.text;
              })
              .join("");
          }
          if (block.type === "heading") {
            const level = "#".repeat(block.level || 1);
            return `${level} ${block.children
              .map((child) => child.text)
              .join("")}`;
          }
          if (block.type === "list") {
            return block.children
              .map(
                (item) =>
                  `- ${item.children.map((child) => child.text).join("")}`
              )
              .join("\n");
          }
          return "";
        })
        .join("\n\n");
    }
    return "";
  };

  // Handle Cloudinary URLs (full URLs) vs local Strapi uploads (relative paths)
  const getImageUrl = (imageObj) => {
    if (!imageObj?.url) return null;

    // If it's already a full URL (Cloudinary), return as-is
    if (imageObj.url.startsWith("http")) {
      return imageObj.url;
    }

    // If it's a relative path (local Strapi), prepend STRAPI_URL
    return `${STRAPI_URL}${imageObj.url}`;
  };

  return {
    id: strapiInserat.id,
    title: attributes.title || "",
    model: attributes.model || "",
    year: attributes.year || "",
    mileage: attributes.mileage || 0,
    engine: attributes.engine || "",
    transmission: attributes.transmission || "",
    overview: getRichText(attributes.overview),
    description: getRichText(attributes.description),
    highlights: getRichText(attributes.highlights),
    price: attributes.price || 0,
    images: Array.isArray(attributes.images)
      ? attributes.images.map((item) => getImageUrl(item)).filter(Boolean)
      : [],
  };
};

// Helper function to transform Instagram post data
export const transformInstagramPost = (strapiPost) => {
  if (!strapiPost) return null;

  const attributes = strapiPost.attributes || strapiPost;

  // Handle Cloudinary URLs (full URLs) vs local Strapi uploads (relative paths)
  const getImageUrl = (imageObj) => {
    if (!imageObj?.url) return null;

    // If it's already a full URL (Cloudinary), return as-is
    if (imageObj.url.startsWith("http")) {
      return imageObj.url;
    }

    // If it's a relative path (local Strapi), prepend STRAPI_URL
    return `${STRAPI_URL}${imageObj.url}`;
  };

  return {
    id: strapiPost.id,
    image: getImageUrl(attributes.image),
    instagramUrl: attributes.instagramUrl || "",
    title: attributes.title || "",
    caption: attributes.caption || "",
    altText: attributes.altText || "",
    publishedAt: attributes.publishedAt || "",
    order: attributes.order || 0,
    isVisible: attributes.isVisible,
  };
};

export default api;
